import { RichTextField } from '@prismicio/client';
import {
  PrismicRichText as BasePrismicRichText,
  JSXMapSerializer,
} from '@prismicio/react';
import { ReactElement, useMemo } from 'react';
import PrismicLink from '@components/PrismicLink/PrismicLink';
import cn from 'classnames';
import { PrismicTextAlignmentField } from '@monofront/types';
import { usePrismicTextAlignmentStyle } from '@hooks/prismic/usePrismicTextAlignmentStyle';
import { usePrismicContentSerializer } from '@hooks/usePrismicContentSerializer';

export type PrismicRichTextProps = {
  field: RichTextField;
  textAlignmentField?: PrismicTextAlignmentField;
  classNames?: { [K in keyof JSXMapSerializer]?: string };
  titleDecorationElement?: ReactElement<any>;
};

export const PrismicRichText = ({
  field,
  textAlignmentField,
  classNames,
  titleDecorationElement,
}: PrismicRichTextProps) => {
  const contentSerializer = usePrismicContentSerializer();

  const alignment = usePrismicTextAlignmentStyle(textAlignmentField);

  const components = useMemo<JSXMapSerializer>(
    () => ({
      em: function PrismicRichTextEm({ children }) {
        return (
          <em className={cn('tw-font-italic', alignment, classNames?.em)}>
            {children}
          </em>
        );
      },
      strong: function PrismicRichTextStrong({ children }) {
        return (
          <strong className={cn('tw-font-bold', alignment, classNames?.strong)}>
            {children}
          </strong>
        );
      },
      list: function PrismicRichTextList({ children }) {
        return (
          <ul
            className={cn('tw-lf-bullet-list tw-text-left', classNames?.list)}
          >
            {children}
          </ul>
        );
      },
      listItem: function PrismicRichTextListItem({ children }) {
        return (
          <li className={cn('tw-lf-bullet-list-item', classNames?.listItem)}>
            {children}
          </li>
        );
      },
      heading1: function PrismicRichTextHeading1({ children }) {
        return (
          <h1 className={cn('tw-font-bold', alignment, classNames?.heading1)}>
            {children}
            {titleDecorationElement}
          </h1>
        );
      },
      heading2: function PrismicRichTextHeading2({ children }) {
        return (
          <h2 className={cn('tw-font-bold', alignment, classNames?.heading2)}>
            {children}
            {titleDecorationElement}
          </h2>
        );
      },
      heading3: function PrismicRichTextHeading3({ children }) {
        return (
          <h3 className={cn('tw-font-bold', alignment, classNames?.heading3)}>
            {children}
            {titleDecorationElement}
          </h3>
        );
      },
      heading4: function PrismicRichTextHeading4({ children }) {
        return (
          <h4 className={cn('tw-font-bold', alignment, classNames?.heading4)}>
            {children}
            {titleDecorationElement}
          </h4>
        );
      },
      heading5: function PrismicRichTextHeading5({ children }) {
        return (
          <h5 className={cn('tw-font-bold', alignment, classNames?.heading5)}>
            {children}
            {titleDecorationElement}
          </h5>
        );
      },
      heading6: function PrismicRichTextHeading6({ children }) {
        return (
          <h6 className={cn('tw-font-bold', alignment, classNames?.heading6)}>
            {children}
            {titleDecorationElement}
          </h6>
        );
      },
      hyperlink: function PrismicRichTextHyperlink({ children, node }) {
        return (
          <PrismicLink
            className="tw-lf-link-inherit tw-lf-link-dark"
            link={node.data}
          >
            {children}
          </PrismicLink>
        );
      },
      paragraph: function PrismicRichTextParagraph({ children }) {
        return (
          <p className={cn('tw-font-normal', alignment, classNames?.paragraph)}>
            {children}
          </p>
        );
      },
      span: function PrismicRichTextSpan({ children, text }) {
        return (
          <span>
            {children && children.length > 0
              ? children
              : contentSerializer(text)}
          </span>
        );
      },
    }),
    [contentSerializer, classNames, alignment, titleDecorationElement],
  );

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <BasePrismicRichText field={field} components={components} />
    </div>
  );
};
